// PortalCreditAccess — /portal/credit-access
//
// Lets a client add or update their credit logins after intake is done. The
// intake link is single-use and expires, so this is the only way back in for
// someone who skipped the step or changed a password.
//
// Passwords are never sent back down — the page shows only whether something
// is on file and which email/username it was saved under.
import { useEffect, useState } from "react";
import { MONITORING_PROVIDERS } from "../lib/api";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, CheckCircle2 } from "lucide-react";
import { PortalShell } from "../components/PortalShell";

async function api(path: string, opts: { method?: string; body?: any } = {}) {
  const res = await fetch(path, {
    method: opts.method || "GET",
    credentials: "include",
    headers: opts.body ? { "Content-Type": "application/json" } : undefined,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 401) { window.location.href = "/portal/login"; throw new Error("unauthorized"); }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
  return data;
}

export default function PortalCreditAccessPage() {
  const [state, setState] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [ckEmail, setCkEmail] = useState("");
  const [ckPassword, setCkPassword] = useState("");

  const [monProvider, setMonProvider] = useState("my_free_score_now");
  const [monUser, setMonUser] = useState("");
  const [monPassword, setMonPassword] = useState("");

  async function load() {
    try {
      const r = await api("/api/portal/credit-access");
      setState(r);
      if (r.credit_karma?.email) setCkEmail(r.credit_karma.email);
      if (r.monitoring?.provider) setMonProvider(r.monitoring.provider);
      if (r.monitoring?.username) setMonUser(r.monitoring.username);
    } catch (e: any) {
      if (e.message !== "unauthorized") setErr(e?.message || "Could not load your credit access.");
    }
  }
  useEffect(() => { load(); }, []);

  async function saveCk() {
    setBusy(true); setMsg(null); setErr(null);
    try {
      await api("/api/portal/credit-access", {
        method: "POST",
        body: { credit_karma: { email: ckEmail.trim(), password: ckPassword } },
      });
      setCkPassword("");
      setMsg("Credit Karma login saved. Your advisor can now pull your pre-qualified offers.");
      load();
    } catch (e: any) { setErr(e?.message || "Could not save that."); }
    finally { setBusy(false); }
  }

  async function saveMon() {
    setBusy(true); setMsg(null); setErr(null);
    try {
      await api("/api/portal/credit-access", {
        method: "POST",
        body: { monitoring: { provider: monProvider, username: monUser.trim(), password: monPassword } },
      });
      setMonPassword("");
      setMsg(monProvider === "experian" || monProvider === "other" ? "Login saved. Your advisor will collect your report from it." : "Monitoring login saved. Your advisor can now import your report.");
      load();
    } catch (e: any) { setErr(e?.message || "Could not save that."); }
    finally { setBusy(false); }
  }

  const ck = state?.credit_karma;
  const mon = state?.monitoring;

  return (
    <PortalShell actions={<Link to="/portal/dashboard" className="portal-btn-ghost"><ArrowLeft size={14} aria-hidden="true" /> Dashboard</Link>}>
      <h1 className="portal-h1" style={{ marginBottom: 4 }}>Credit access</h1>
      <p className="portal-sub" style={{ marginBottom: 8 }}>
        Add or update the logins your advisor uses to pull your report and find the offers you're
        already pre-qualified for.
      </p>
      <p className="portal-sub" style={{ marginBottom: 22, fontSize: 13 }}>
        <ShieldCheck size={14} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 6 }} />
        Encrypted the moment you save. Used only for your file, never shared, and never shown back
        to anyone in plain text.
      </p>

      {msg && (
        <article className="portal-card" style={{ marginBottom: 16 }}>
          <p style={{ margin: 0, color: "var(--ne-success)" }}>
            <CheckCircle2 size={15} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 6 }} />{msg}
          </p>
        </article>
      )}
      {err && <p className="portal-sub" style={{ color: "var(--ne-red-dark)", marginBottom: 14 }}>{err}</p>}
      {!state && !err && <p className="portal-sub">Loading…</p>}

      {state && (
        <>
          {/* ── Credit Karma ── */}
          <article className="portal-card" style={{ marginBottom: 16 }}>
            <div className="portal-card-heading"><h2>Credit Karma</h2></div>
            <p className="portal-sub on-card" style={{ fontSize: 13 }}>
              {ck?.on_file
                ? `On file for ${ck.email}. Enter your password again below to update it.`
                : "Credit Karma is free, and it's what shows the offers you're already pre-qualified for."}
            </p>
            <label className="portal-field" style={{ display: "block", marginTop: 12 }}>
              <span className="portal-sub on-card" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Credit Karma email</span>
              <input value={ckEmail} onChange={(e) => setCkEmail(e.target.value)} inputMode="email"
                autoComplete="off" style={{ width: "100%" }} placeholder="you@example.com" />
            </label>
            <label className="portal-field" style={{ display: "block", marginTop: 12 }}>
              <span className="portal-sub on-card" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Credit Karma password</span>
              <input type="password" value={ckPassword} onChange={(e) => setCkPassword(e.target.value)}
                autoComplete="new-password" style={{ width: "100%" }} placeholder={ck?.on_file ? "•••••••• saved" : ""} />
            </label>
            <button className="portal-btn-primary wide" style={{ marginTop: 14 }}
              disabled={busy || !ckEmail.trim() || !ckPassword}
              onClick={saveCk}>
              {busy ? "Saving…" : ck?.on_file ? "Update Credit Karma login" : "Save Credit Karma login"}
            </button>
          </article>

          {/* ── Monitoring ── */}
          <article className="portal-card">
            <div className="portal-card-heading"><h2>Credit monitoring</h2></div>
            <p className="portal-sub on-card" style={{ fontSize: 13 }}>
              {mon?.on_file
                ? `On file for ${mon.username}. Enter your password again below to update it.`
                : "This is the account your full credit report is pulled from."}
            </p>
            <label className="portal-field" style={{ display: "block", marginTop: 12 }}>
              <span className="portal-sub on-card" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Which service</span>
              <select value={monProvider} onChange={(e) => setMonProvider(e.target.value)} style={{ width: "100%" }}>
                {MONITORING_PROVIDERS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </label>
            <label className="portal-field" style={{ display: "block", marginTop: 12 }}>
              <span className="portal-sub on-card" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Username or email</span>
              <input value={monUser} onChange={(e) => setMonUser(e.target.value)} autoComplete="off" style={{ width: "100%" }} />
            </label>
            <label className="portal-field" style={{ display: "block", marginTop: 12 }}>
              <span className="portal-sub on-card" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Password</span>
              <input type="password" value={monPassword} onChange={(e) => setMonPassword(e.target.value)}
                autoComplete="new-password" style={{ width: "100%" }} placeholder={mon?.on_file ? "•••••••• saved" : ""} />
            </label>
            <button className="portal-btn-primary wide" style={{ marginTop: 14 }}
              disabled={busy || !monUser.trim() || !monPassword}
              onClick={saveMon}>
              {busy ? "Saving…" : mon?.on_file ? "Update monitoring login" : "Save monitoring login"}
            </button>
          </article>
        </>
      )}
    </PortalShell>
  );
}
